import { useEffect, useRef, useState } from 'react';
import type * as MapLibre from 'maplibre-gl';
import type { Map as MapLibreMap, Marker as MapLibreMarker } from 'maplibre-gl';
import { Icon } from '../atoms/Icon';
import { Text } from '../atoms/Text';
import styles from './MapView.module.css';

export interface GeoPosition {
  latitude: number;
  longitude: number;
}

export interface MapMarker extends GeoPosition {
  id: string;
  label: string;
  /** Small number or text on the pin, e.g. the number of colonies. */
  badge?: string | number;
  /** `muted` for inactive entries. */
  tone?: 'accent' | 'muted';
  onSelect?: () => void;
}

export interface MapViewProps {
  /** Raster tile URL template with `{z}/{x}/{y}` (configurable for self-hosting). */
  tileUrl: string;
  attribution: string;
  markers?: MapMarker[];
  /** Id of a marker to emphasize, e.g. while its list row is hovered. */
  highlight?: string | null;
  /** A picked position, shown as draggable pin (e.g. while editing). */
  pick?: GeoPosition | null;
  /** Clicking the map or dragging the pin picks a position. */
  onPick?: (position: GeoPosition) => void;
  /** Initial center; otherwise the map fits all markers. */
  center?: GeoPosition;
  zoom?: number;
  /** CSS height, default `22rem`. */
  height?: string;
  /** Accessible name of the map. */
  label: string;
  /** Texts (i18n). */
  labels: {
    zoomIn: string;
    zoomOut: string;
    unavailable: string;
    /** Shown over the map while its background tiles do not load (#82). */
    tilesUnavailable?: string;
    /** Name of a cluster of overlapping pins, e.g. "3 Standorte". */
    cluster?: (count: number) => string;
    /** Full-screen button; without both texts the map has none. */
    fullscreen?: string;
    exitFullscreen?: string;
  };
}

const GERMANY: GeoPosition = { latitude: 51.16, longitude: 10.45 };

interface Pin {
  id: string;
  el: HTMLElement;
  marker: MapLibreMarker;
  position: GeoPosition;
  badge: string | number | undefined;
  muted: boolean;
}

/** A pin or cluster on screen; names of highlighted and heavier ones win. */
interface Shown {
  el: HTMLElement;
  highlighted: boolean;
  weight: number;
}

const weight = (badge: string | number | undefined) => (typeof badge === 'number' ? badge : 0);

type Priority = Pick<Shown, 'highlighted' | 'weight'>;

const byPriority = (a: Priority, b: Priority) =>
  Number(b.highlighted) - Number(a.highlighted) || b.weight - a.weight;

const overlaps = (a: DOMRect, b: DOMRect) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

const rectOf = (el: HTMLElement, className: string | undefined) =>
  el.querySelector(`.${className}`)?.getBoundingClientRect();

/** A pin: badge with tip, name below. A button, so it works with the keyboard. */
function pinElement(label: string, badge: string | number | undefined, muted: boolean) {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = [styles.pin, muted && styles.muted].filter(Boolean).join(' ');
  el.setAttribute('aria-label', label);
  el.title = label;
  const badgeEl = document.createElement('span');
  badgeEl.className = styles.badge ?? '';
  badgeEl.textContent = badge === undefined ? '' : String(badge);
  el.append(badgeEl);
  const name = document.createElement('span');
  name.className = styles.name ?? '';
  name.textContent = label;
  el.append(name);
  return el;
}

/**
 * Hides names that would cover another pin or a name already shown. Hidden
 * names appear on hover and focus.
 */
function declutterNames(shown: Shown[]) {
  const hidden = styles.nameHidden ?? 'nameHidden';
  const entries = shown.map((s) => ({ ...s, badge: rectOf(s.el, styles.badge) }));
  const placed: DOMRect[] = [];
  for (const entry of [...entries].sort(byPriority)) {
    const name = entry.el.querySelector(`.${styles.name}`);
    if (!name) continue;
    const rect = name.getBoundingClientRect();
    const hit =
      placed.some((r) => overlaps(r, rect)) ||
      entries.some((o) => o !== entry && o.badge !== undefined && overlaps(o.badge, rect));
    name.classList.toggle(hidden, hit);
    if (!hit) placed.push(rect);
  }
}

function supportsWebGl(): boolean {
  if (typeof window === 'undefined' || typeof window.WebGLRenderingContext === 'undefined') {
    return false;
  }
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

function cssVar(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Map with OpenStreetMap compatible raster tiles (MapLibre GL, loaded on
 * demand so it does not weigh on the initial bundle). Markers are buttons,
 * so they work with the keyboard. Without WebGL it shows a notice; callers
 * should offer the same information as a list next to the map.
 */
export function MapView({
  tileUrl,
  attribution,
  markers = [],
  highlight,
  pick,
  onPick,
  center,
  zoom = 13,
  height = '22rem',
  label,
  labels,
}: MapViewProps) {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const libRef = useRef<typeof MapLibre | null>(null);
  const markerRefs = useRef<MapLibreMarker[]>([]);
  const pins = useRef<Pin[]>([]);
  const highlightRef = useRef(highlight);
  highlightRef.current = highlight;
  /** Cluster markers by their sorted member ids. */
  const clusterRefs = useRef<Map<string, MapLibreMarker>>(new Map());
  /** Pins fanned out side by side because zooming cannot separate them. */
  const fanned = useRef<{ ids: string[]; zoom: number } | null>(null);
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  /**
   * Pins whose badges overlap merge into one cluster pin with the summed
   * badge. Clicking it zooms in until they separate; if they stand (almost)
   * on the same spot, it fans them out side by side instead. Then names that
   * would still collide are hidden.
   */
  const layout = () => {
    const map = mapRef.current;
    const lib = libRef.current;
    if (!map || !lib) return;
    // Clusters with the same members are kept, so they stay hoverable while the map moves.
    const previous = clusterRefs.current;
    clusterRefs.current = new Map();
    const clustered = styles.clustered ?? 'clustered';
    const all = pins.current;
    if (fanned.current && map.getZoom() < fanned.current.zoom - 0.5) fanned.current = null;
    const fan = fanned.current?.ids ?? [];
    all.forEach((p) => {
      p.el.classList.remove(clustered);
      const k = fan.indexOf(p.id);
      p.marker.setOffset(k < 0 ? [0, 0] : [(k - (fan.length - 1) / 2) * 44, 0]);
    });
    // Fanned-out pins never merge again until the user zooms out.
    const entries = all
      .map((pin) => ({
        pin,
        highlighted: pin.id === highlightRef.current,
        weight: weight(pin.badge),
        badge: fan.includes(pin.id) ? undefined : rectOf(pin.el, styles.badge),
      }))
      .sort(byPriority);
    const taken = new Set<Pin>();
    const shown: Shown[] = [];
    for (const entry of entries) {
      if (taken.has(entry.pin)) continue;
      const own = entry.badge;
      const group = entries.filter(
        (o) =>
          !taken.has(o.pin) &&
          (o === entry || (own !== undefined && o.badge !== undefined && overlaps(own, o.badge))),
      );
      group.forEach((o) => taken.add(o.pin));
      const members = group.map((o) => o.pin);
      const highlighted = group.some((o) => o.highlighted);
      if (members.length === 1) {
        shown.push({ el: entry.pin.el, highlighted, weight: entry.weight });
        continue;
      }
      members.forEach((m) => m.el.classList.add(clustered));
      const total = members.every((m) => typeof m.badge === 'number')
        ? members.reduce((sum, m) => sum + weight(m.badge), 0)
        : members.length;
      const key = members
        .map((m) => m.id)
        .sort()
        .join(' ');
      const marker = previous.get(key) ?? clusterMarker(map, lib, members, total);
      previous.delete(key);
      clusterRefs.current.set(key, marker);
      const el = marker.getElement();
      el.classList.toggle(styles.active ?? 'active', highlighted);
      shown.push({ el, highlighted, weight: weight(total) });
    }
    previous.forEach((m) => m.remove());
    declutterNames(shown);
  };

  /** A cluster pin at the members' centre: zooms in on click, or fans them out. */
  const clusterMarker = (map: MapLibreMap, lib: typeof MapLibre, members: Pin[], total: number) => {
    const el = pinElement(
      labelsRef.current.cluster?.(members.length) ?? String(members.length),
      total,
      members.every((m) => m.muted),
    );
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const bounds = new lib.LngLatBounds();
      members.forEach((m) => bounds.extend([m.position.longitude, m.position.latitude]));
      const camera = map.cameraForBounds(bounds, { padding: 80, maxZoom: 17 });
      if (camera?.zoom !== undefined && camera.zoom > map.getZoom() + 0.5) {
        // Linear: zoom straight in, without flying out first.
        map.fitBounds(bounds, { padding: 80, maxZoom: 17, linear: true });
      } else {
        fanned.current = { ids: members.map((m) => m.id), zoom: map.getZoom() };
        layout();
      }
    });
    const avg = (f: (p: GeoPosition) => number) =>
      members.reduce((sum, m) => sum + f(m.position), 0) / members.length;
    return new lib.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([avg((p) => p.longitude), avg((p) => p.latitude)])
      .addTo(map);
  };
  // Marker positions update on the map's own `move` handlers; measure a frame later.
  const frame = useRef(0);
  const layoutSoon = () => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(layout);
  };
  const pickRef = useRef<MapLibreMarker | null>(null);
  const onPickRef = useRef(onPick);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [tilesFailed, setTilesFailed] = useState(false);
  const [full, setFull] = useState(false);
  const canFullscreen = Boolean(labels.fullscreen && labels.exitFullscreen);
  onPickRef.current = onPick;

  useEffect(() => {
    if (!supportsWebGl()) {
      setState('unavailable');
      return;
    }
    let cancelled = false;
    let map: MapLibreMap | null = null;
    void Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl.css')])
      .then(([lib]) => {
        if (cancelled || !container.current) return;
        const start = center ?? markers[0] ?? pick ?? GERMANY;
        map = new lib.Map({
          container: container.current,
          style: {
            version: 8,
            sources: {
              tiles: { type: 'raster', tiles: [tileUrl], tileSize: 256, attribution },
            },
            layers: [
              {
                id: 'background',
                type: 'background',
                paint: { 'background-color': cssVar('--color-surface-sunken', '#f3f0ea') },
              },
              { id: 'tiles', type: 'raster', source: 'tiles' },
            ],
          },
          // Tile servers like OpenStreetMap reject requests without a referrer;
          // an explicit policy wins over a browser default of `no-referrer`.
          transformRequest: (url) => ({ url, referrerPolicy: 'strict-origin-when-cross-origin' }),
          center: [start.longitude, start.latitude],
          zoom: center || markers.length > 0 || pick ? zoom : 5,
          attributionControl: { compact: true },
          locale: {
            'NavigationControl.ZoomIn': labels.zoomIn,
            'NavigationControl.ZoomOut': labels.zoomOut,
          },
          cooperativeGestures: false,
        });
        map.addControl(new lib.NavigationControl({ showCompass: false }), 'top-right');
        map.on('click', (e) =>
          onPickRef.current?.({ latitude: e.lngLat.lat, longitude: e.lngLat.lng }),
        );
        map.on('move', layoutSoon);
        // A failing tile source leaves the neutral background and a hint;
        // pins and the list next to the map stay usable (#82).
        map.on('error', (e: { sourceId?: string }) => {
          if (e.sourceId === 'tiles') setTilesFailed(true);
        });
        map.on('sourcedata', (e) => {
          if (e.sourceId === 'tiles' && e.tile && e.tile.state === 'loaded') setTilesFailed(false);
        });
        mapRef.current = map;
        libRef.current = lib;
        setState('ready');
      })
      .catch(() => setState('unavailable'));
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame.current);
      map?.remove();
      mapRef.current = null;
    };
    // The map is created once per tile source; markers update separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileUrl, attribution]);

  // Full screen: the frame covers the viewport (a sheet on phones); the map keeps its state.
  useEffect(() => {
    mapRef.current?.resize();
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFull(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [full]);

  // Markers.
  useEffect(() => {
    const map = mapRef.current;
    const lib = libRef.current;
    if (state !== 'ready' || !map || !lib) return;
    markerRefs.current.forEach((m) => m.remove());
    clusterRefs.current.forEach((m) => m.remove());
    clusterRefs.current = new Map();
    pins.current = [];
    fanned.current = null;
    markerRefs.current = markers.map((m) => {
      const muted = m.tone === 'muted';
      const el = pinElement(m.label, m.badge, muted);
      el.classList.toggle(styles.active ?? 'active', m.id === highlightRef.current);
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        m.onSelect?.();
      });
      const marker = new lib.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([m.longitude, m.latitude])
        .addTo(map);
      pins.current.push({ id: m.id, el, marker, position: m, badge: m.badge, muted });
      return marker;
    });
    if (!center && !pick && markers.length > 1) {
      const bounds = new lib.LngLatBounds();
      markers.forEach((m) => bounds.extend([m.longitude, m.latitude]));
      map.fitBounds(bounds, {
        padding: { top: 80, bottom: 60, left: 80, right: 80 },
        maxZoom: 13,
        duration: 0,
      });
    } else if (!center && !pick && markers.length === 1 && markers[0]) {
      map.jumpTo({ center: [markers[0].longitude, markers[0].latitude], zoom });
    }
    layoutSoon();
    // layoutSoon only reads refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, markers, center, pick, zoom]);

  // Highlighted marker.
  useEffect(() => {
    if (state !== 'ready') return;
    pins.current.forEach((p) =>
      p.el.classList.toggle(styles.active ?? 'active', p.id === highlight),
    );
    layoutSoon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, highlight]);

  // Picked position.
  useEffect(() => {
    const map = mapRef.current;
    const lib = libRef.current;
    if (state !== 'ready' || !map || !lib) return;
    if (!pick) {
      pickRef.current?.remove();
      pickRef.current = null;
      return;
    }
    if (!pickRef.current) {
      const el = document.createElement('div');
      el.className = styles.pickPin ?? '';
      pickRef.current = new lib.Marker({
        element: el,
        anchor: 'bottom',
        draggable: Boolean(onPickRef.current),
      })
        .setLngLat([pick.longitude, pick.latitude])
        .addTo(map);
      pickRef.current.on('dragend', () => {
        const p = pickRef.current?.getLngLat();
        if (p) onPickRef.current?.({ latitude: p.lat, longitude: p.lng });
      });
      map.jumpTo({ center: [pick.longitude, pick.latitude], zoom: Math.max(map.getZoom(), zoom) });
    } else {
      pickRef.current.setLngLat([pick.longitude, pick.latitude]);
      if (!map.getBounds().contains([pick.longitude, pick.latitude])) {
        map.easeTo({ center: [pick.longitude, pick.latitude] });
      }
    }
  }, [state, pick, zoom]);

  return (
    <div className={styles.slot} style={{ height }}>
      <div className={[styles.frame, full && styles.full].filter(Boolean).join(' ')}>
        <div ref={container} className={styles.map} role="region" aria-label={label} />
        {state === 'ready' && canFullscreen && (
          <button
            type="button"
            className={styles.fullscreenButton}
            aria-label={full ? labels.exitFullscreen : labels.fullscreen}
            aria-pressed={full}
            onClick={() => setFull((f) => !f)}
          >
            <Icon name={full ? 'collapse' : 'expand'} size="sm" />
          </button>
        )}
        {state === 'ready' && tilesFailed && labels.tilesUnavailable && (
          <div className={styles.tilesHint} role="status">
            <Icon name="alert-circle" size="sm" />
            {labels.tilesUnavailable}
          </div>
        )}
        {state === 'unavailable' && (
          <div className={styles.fallback}>
            <Icon name="map" size="lg" />
            <Text size="sm" tone="muted">
              {labels.unavailable}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
